// ── The ONE shared <Text> render contract (Audit A #1 / #118 codemod foundation)
//
// components/Text/index.js (<Text>, client) and components/Text/TextServer.jsx
// (<TextServer>, RSC) used to carry a byte-for-byte DUPLICATED copy of the
// variant→tag/class map, the trademark (®) + highlight content styling, and the
// element-building logic — two copies that had to be kept in lock-step BY HAND
// on the single most-used, hydration-sensitive primitive in the app. This module
// is that logic, extracted ONCE as pure functions. Both leaves are now thin
// wrappers over it, so they render identical markup BY CONSTRUCTION and can never
// drift (the drift gap is now also closed by the __tests__/Server.test.js parity
// suite). Mirrors the Img/ImgServer factoring in components/Img/resolve.js.
//
// PURE + RSC-SAFE: no hooks, no context, no Overmind, no browser APIs. Imports
// nothing manifest-backed (only the CSS-module class map + reactStringReplace, a
// pure string→nodes helper), so a Server Component can import it without tripping
// the RSC build boundary. The client/server split lives ENTIRELY in the two
// wrappers: the client one adds Overmind price substitution + the MyLoader
// skeleton; the server one is the no-price passthrough. The render contract here
// takes neither.

import React from 'react'
import styles from './styles.module.css'
import reactStringReplace from 'react-string-replace'

// ── Variant contract ────────────────────────────────────────────────────────
//
// Per-variant descriptor: the DOM tag each variant renders, the CSS-module class,
// and whether the variant supported the loader-skeleton `hide` visibility toggle.
//
// SEO / single-<h1> rule (Audit A #1). The document <h1> is NOT a property of any
// VARIANT — it is a FIRST-CLASS, explicit render-contract flag: pass `primary` to
// mark a template's ONE keyword-rich hero heading, and `resolveTextRender` forces
// its tag to <h1> while keeping the variant's visual class (so the heading looks
// exactly like its `heading-1`/`heading-3`/`h1-hero` design, but IS the page's
// single <h1>). Because `primary` — not the variant — owns the <h1>, the `h1`/`h2`
// variants (reused for section titles AND the PricingOffer price cells) render as
// <h2> and can NEVER become a stray document <h1>. `h1-hero` is likewise just a
// giant-display VISUAL variant now (tag <h2>); the hardware hero opts in to the
// document <h1> via `primary`, exactly like every other template. Grep `primary`
// across components/ to find the (one-per-page) primary headings.
export const VARIANTS = {
  tiny: { tag: 'div', className: styles.tiny },
  small: { tag: 'div', className: styles.small, hideable: true },
  medium: { tag: 'div', className: styles.medium, hideable: true },
  regular: { tag: 'div', className: styles.regular },
  large: { tag: 'div', className: styles.large },
  'heading-1': { tag: 'div', className: styles.heading1, hideable: true },
  'heading-2': { tag: 'div', className: styles.heading2 },
  'heading-3': { tag: 'div', className: styles.heading3 },
  'heading-4': { tag: 'div', className: styles.heading4 },
  'heading-5': { tag: 'div', className: styles.heading5, hideable: true },
  'heading-6': { tag: 'div', className: styles.heading6, hideable: true },
  'heading-7': { tag: 'div', className: styles.heading7, hideable: true },
  'heading-8': { tag: 'div', className: styles.heading8, hideable: true },
  'heading-9': { tag: 'div', className: styles.heading9, hideable: true },
  'heading-10': { tag: 'div', className: styles.heading10, hideable: true },
  'button-1': { tag: 'div', className: styles.button1 },
  'button-small-1': { tag: 'div', className: styles.buttonSmall1 },
  'body-long-2': { tag: 'p', className: styles.bodyLong2 },
  'body-short-2': { tag: 'p', className: styles.bodyShort2 },
  'body-long-1': { tag: 'p', className: styles.bodyLong1 },
  'body-short-1': { tag: 'p', className: styles.bodyShort1 },
  'caption-1': { tag: 'div', className: styles.caption1 },
  'caption-2': { tag: 'div', className: styles.caption2 },
  // Giant hero DISPLAY variant. Tag is <h2> — the document <h1> comes from the
  // `primary` flag, not from this variant (see the single-<h1> note above).
  'h1-hero': { tag: 'h2', className: styles.heroH1 },
  h1: { tag: 'h2', className: styles.h1 },
  h2: { tag: 'h2', className: styles.h2 },
  h3: { tag: 'h3', className: styles.h3 },
  h4: { tag: 'h4', className: styles.h4 },
  h5: { tag: 'h5', className: styles.h5 },
  'product-name': { tag: 'h4', className: styles.productName },
  'product-price': { tag: 'h4', className: styles.productPrice },
  'product-optional-info': { tag: 'div', className: styles.productOptionalInfo },
  'shipping-info': { tag: 'h5', className: styles.shippingInfo },
  'shipping-info-long': { tag: 'h5', className: styles.shippingInfoLong },
  'shipping-info-subtext': { tag: 'div', className: styles.shippingInfoSubtext },
  'download-item-name': { tag: 'h4', className: styles.downloadItemName },
  'file-name': { tag: 'h5', className: styles.fileName },
  announcement: { tag: 'div', className: styles.announcement },
}

// Fallback matches the old `default:` branch (BodyLong1 => <p class=bodyLong1>).
export const DEFAULT_VARIANT = { tag: 'p', className: styles.bodyLong1 }

// The set of valid `type` keys (used by the client wrapper's dev validation).
export const types = Object.keys(VARIANTS)

// Resolve a `type` (case-insensitive) to its variant descriptor, or the default.
export const resolveVariant = (type) => VARIANTS[String(type).toLowerCase()] || DEFAULT_VARIANT

// Parse a raw CSS declaration string (e.g. "color: red; font-weight: 500;") into
// a React inline-style object. Callers pass `highlightSx`/`color` as CSS text —
// the former styled-components interpolated that text directly; React inline
// styles need an object, so we translate here to preserve the identical output.
export const cssStringToStyleObject = (css) => {
  const style = {}
  css.split(';').forEach((decl) => {
    const idx = decl.indexOf(':')
    if (idx < 0) {
      return
    }
    const prop = decl.slice(0, idx).trim()
    const value = decl.slice(idx + 1).trim()
    if (!prop || !value) {
      return
    }
    // Convert kebab-case CSS property to camelCase React style key.
    const key = prop.replace(/-([a-z])/g, (_m, c) => c.toUpperCase())
    style[key] = value
  })
  return style
}

const replaceTrademarkSymbolWithHTMLTag = (str) => {
  // replace part of string(trademark symbol) with tag in JSX, read more here https://noto.li/8k9BPu
  return reactStringReplace(str, '®', (match, i) => {
    return <span key={`tm-${i}`} className={styles.specialCharacter}>{match}</span>
  })
}

const styleTrademarkSymbol = (children) => {
  if (!children) {
    return children
  }

  if (Array.isArray(children)) {
    return children.map((c) => replaceTrademarkSymbolWithHTMLTag(c))
  } else {
    return replaceTrademarkSymbolWithHTMLTag(children)
  }
}

const highlightText = (str, highlight, sx, exactMatch) => {
  let highlightParts
  if (exactMatch) {
    highlightParts = highlight.split('|').map((part) => part.trim())
  } else {
    highlightParts = highlight.split('|').flatMap((part) => part.trim().split(/\s+/))
  }

  // Match the former styled-component precedence EXACTLY: `props.sx ? sx :
  // 'background-color: #ff9632;'` — the sx REPLACES the default (it does not
  // layer on top of it). So when `sx` is present we apply ONLY the sx (as an
  // inline style, translated from its raw-CSS-string form) and DROP the default
  // background class; otherwise we apply the default `.markedSearchTerm` class.
  const style = sx ? cssStringToStyleObject(sx) : undefined
  const className = sx ? undefined : styles.markedSearchTerm

  highlightParts.forEach((h) => {
    str = reactStringReplace(str, h, (match, i) => {
      return <span key={`hl-${i}`} className={className} style={style}>{match}</span>
    })
  })

  return str
}

const styleHighlightedText = (children, highlight, sx, exactMatch) => {
  if (!children) {
    return children
  }

  if (Array.isArray(children)) {
    return children.map((c) => highlightText(c, highlight, sx, exactMatch))
  } else {
    return highlightText(children, highlight, sx, exactMatch)
  }
}

/**
 * Apply the shared, pure content styling to a Text's children: the trademark (®)
 * superscript wrap, then (when `highlight` is set) the highlight-term wrap. Order
 * matches the historical twin: trademark first, highlight second. The client
 * wrapper feeds this its PRICE-SUBSTITUTED content; the server wrapper feeds it
 * the raw children — for the no-price case both are identical (parity).
 */
export const applyContentStyling = (children, { highlight, highlightSx, highlightExactMatch } = {}) => {
  let content = styleTrademarkSymbol(children)
  content = highlight ? styleHighlightedText(content, highlight, highlightSx, highlightExactMatch) : content
  return content
}

/**
 * @typedef {object} TextRenderModel
 * @property {string} Tag        the DOM tag to render (variant tag, or 'h1' when `primary`)
 * @property {string} className  the composed className (variant class [+ hide] [+ passthrough])
 * @property {React.CSSProperties|undefined} style  inline style ({color} or undefined)
 */

/**
 * Resolve a `type` + presentation props into the flat element model both <Text>
 * and <TextServer> paint. Pure — no hooks/context. The `primary` flag is the ONE
 * authority for the document <h1> (see the VARIANTS note): when set, the tag is
 * forced to <h1> while the visual class is kept, so the primary hero heading of a
 * template IS its single <h1> without any variant being "special".
 *
 * @param {object} params
 * @param {string} params.type          the Text variant key (case-insensitive)
 * @param {string} [params.className]    passthrough class appended after the variant class
 * @param {string} [params.color]        dynamic text color → inline style
 * @param {boolean} [params.hide]        loader-skeleton visibility toggle (adds .hide)
 * @param {boolean} [params.primary]     mark as the page's single primary <h1>
 * @returns {TextRenderModel}
 */
export const resolveTextRender = ({ type, className, color, hide = false, primary = false }) => {
  const variant = resolveVariant(type)
  const Tag = primary ? 'h1' : variant.tag
  const baseClass = hide ? `${variant.className} ${styles.hide}` : variant.className
  const finalClassName = className ? `${baseClass} ${className}` : baseClass
  const style = color ? { color } : undefined
  return { Tag, className: finalClassName, style }
}
